import { useCallback, useEffect, useRef, useState } from 'react';
import { copyToClipboard } from '@/utils/clipboard';

/**
 * Copy-to-clipboard with transient "copied" feedback.
 *
 * `copiedKey` is the key passed to the last successful `copy` and resets to
 * null after `resetMs`, so one hook can drive several copy buttons.
 */
export function useClipboard(resetMs = 2000) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const copy = useCallback(
    async (text: string, key = 'default'): Promise<boolean> => {
      const ok = await copyToClipboard(text);
      if (ok) {
        if (timerRef.current) clearTimeout(timerRef.current);
        setCopiedKey(key);
        timerRef.current = setTimeout(() => setCopiedKey(null), resetMs);
      }
      return ok;
    },
    [resetMs]
  );

  return { copiedKey, copy };
}
